import { useRef, useEffect } from "react";
import { Heart } from "lucide-react";
import { BRAND_COLOR } from "@/lib/constants";
import { useDanceStyles } from "@/hooks/useDanceStyles";
import { cn } from "@/lib/utils";

interface FilterBarProps {
  selectedStyles: string[];
  onStyleChange: (styles: string[]) => void;
  showFavoritesOnly: boolean;
  onFavoritesChange: (show: boolean) => void;
}

export function FilterBar({
  selectedStyles,
  onStyleChange,
  showFavoritesOnly,
  onFavoritesChange,
}: FilterBarProps) {
  const { styles } = useDanceStyles();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current && selectedStyles.length > 0) {
      const lastStyle = selectedStyles[selectedStyles.length - 1];
      const button = scrollRef.current.querySelector(`[data-style="${lastStyle}"]`);
      button?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [selectedStyles]);

  const handleStyleClick = (slug: string) => {
    if (selectedStyles.includes(slug)) {
      onStyleChange(selectedStyles.filter((s) => s !== slug));
    } else {
      onStyleChange([...selectedStyles, slug]);
    }
  };

  return (
    <div className="relative -mx-4 px-4">
      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

      {/* Scrollable container */}
      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto scrollbar-hide py-0.5"
      >
        {/* Favorites button */}
        <FilterPill
          label="Favorites"
          isSelected={showFavoritesOnly}
          color="#E07A9E"
          onClick={() => onFavoritesChange(!showFavoritesOnly)}
          icon={<Heart className="w-3.5 h-3.5" fill={showFavoritesOnly ? "currentColor" : "none"} />}
        />

        {/* All Styles button */}
        <FilterPill
          label="All Styles"
          isSelected={selectedStyles.length === 0}
          color={BRAND_COLOR}
          onClick={() => onStyleChange([])}
        />

        {/* Dance style buttons */}
        {styles.map((style) => (
          <FilterPill
            key={style.slug}
            label={style.name}
            isSelected={selectedStyles.includes(style.slug)}
            color={style.color}
            onClick={() => handleStyleClick(style.slug)}
            data-style={style.slug}
          />
        ))}
      </div>
    </div>
  );
}

interface FilterPillProps {
  label: string;
  isSelected: boolean;
  color: string;
  onClick: () => void;
  icon?: React.ReactNode;
  "data-style"?: string;
}

function FilterPill({ label, isSelected, color, onClick, icon, ...props }: FilterPillProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex-shrink-0 px-3.5 py-2 rounded-full text-sm font-medium transition-all duration-200",
        "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "flex items-center gap-1.5",
        isSelected
          ? "text-white"
          : "bg-secondary text-foreground/80 hover:text-foreground hover:bg-secondary/80"
      )}
      style={
        isSelected
          ? { backgroundColor: color }
          : undefined
      }
      {...props}
    >
      {icon}
      {label}
    </button>
  );
}
